import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setOrganizationId, setSelectedOrganization } from "./authSlice";
import { storageLoad } from "../../utils/localStorage";

export const useOrganizationId = (page = 'default') => {
	const dispatch = useDispatch();
	const organizationName = useSelector((state) => state.auth.organizationName);
	const selectedOrganization = useSelector((state) => state.auth.selectedOrganization);
	const organizationId = useSelector((state) => state.auth.organizationId);
	const userDetails = useSelector((state) => state.auth.userDetails);

	// Only count organizations where login_enabled is not explicitly false
	const enabledOrgs = useMemo(
		() => userDetails?.organizations?.filter(org => org.login_enabled !== false) || [],
		[userDetails?.organizations]
	);

	// Check if user is multi-org (using only enabled orgs)
	const isMultiOrgUser = enabledOrgs.length > 1;

	// For specific pages (pipeline, home, upload-history), apply fallback logic
	// For new-loan page, require explicit selection
	const shouldApplyFallback = ['pipeline', 'home', 'upload-history'].includes(page);
	
	const getFallbackOrgName = () => {
		if (isMultiOrgUser && !selectedOrganization && shouldApplyFallback && enabledOrgs.length > 0) {
			// For multi-org users with same org name but different channels, just pick the first one
			// This avoids the ambiguity of trying to match by name + channel
			return enabledOrgs[0]?.name;
		}
		// Handle case where organizationName might be an array (shouldn't happen but defensive programming)
		if (Array.isArray(organizationName)) {
			return organizationName[0] || selectedOrganization;
		}
		return selectedOrganization || organizationName;
	};

	const currentOrgName = getFallbackOrgName();

	useEffect(() => {
		// Auto-select organization for multi-org users only on specific pages if none is selected
		if (isMultiOrgUser && !selectedOrganization && shouldApplyFallback && enabledOrgs.length > 0) {
			// Check localStorage first so the user's saved selection survives a page refresh
			const savedOrg = storageLoad("selected-organization");
			const savedOrgID = storageLoad("selected-organization-id");
			const savedOrgIsValid = savedOrg && enabledOrgs.some(org => org.name === savedOrg);
			if (savedOrgIsValid) {
				dispatch(setSelectedOrganization({name: savedOrg, id: savedOrgID}));
				dispatch(setOrganizationId(savedOrgID));
			} else {
				// Fall back to the first enabled organization
				const firstOrg = enabledOrgs[0];
				if (firstOrg?.name) {
					dispatch(setSelectedOrganization({ name: firstOrg.name, id: firstOrg.id }));
					dispatch(setOrganizationId(firstOrg.id));
				}
			}
		}
	}, [dispatch, enabledOrgs, isMultiOrgUser, selectedOrganization, shouldApplyFallback, userDetails]);

	useEffect(() => {
		// Only set organization ID if we have a current org name and user details
		if (currentOrgName && userDetails?.organizations) {
			// Find the organization by exact name match (including channel in parentheses)
			const organization = userDetails.organizations.find(org => org.name === currentOrgName);
			const savedOrgID = storageLoad("selected-organization-id");
			if (savedOrgID) {
				dispatch(setOrganizationId(savedOrgID));
				console.log("Debug1",savedOrgID)
			} else {
				dispatch(setOrganizationId(organization?.id));
				console.log("Debug11",organization?.id)
			}
		}
	}, [currentOrgName, dispatch, userDetails?.organizations]);

	return organizationId;
};
